// @ts-check
// Panel "Contenido del bloque seleccionado" (TSK-0050).
//
// Contexto (ver cabecera de expression-editor.js, TSK-0049, y el comentario
// de TSK-0022 en palette-view.js): el editor visual coloca bloques con
// contenido de RELLENO fijo (p.ej. "Entrada" siempre crea
// `variable: 'variable'`) porque hasta ahora ningún panel permitía editar el
// contenido real de un bloque ya colocado. Este módulo es esa pieza: dado un
// bloque seleccionado en el canvas, muestra y permite editar sus campos
// primitivos reales y, para las expresiones, monta una instancia de
// `expression-editor.js` (TSK-0049) tal cual, sin reimplementar su lógica.
//
// Mismo contrato/patrón que `block-operations.js` (TSK-0024, "done", en
// rutas_prohibidas): este panel es un componente AUTÓNOMO (no importa
// `canvas-view.js`) que consume la selección del canvas "por contrato de
// DOM": si se proporciona `canvasRoot` (el `elemento` que devuelve
// `crearVistaCanvas`), escucha `focusin` sobre esa raíz y lee
// `dataset.nodeId` del elemento enfocado (el mismo atributo que ya asigna
// `canvas-view.js` a cada bloque: `boton.dataset.nodeId = nodo.id`). Misma
// live region moderada (`role="status"`, `aria-live="polite"`,
// `aria-atomic="true"`) que `block-operations.js`/`canvas-view.js`.
//
// Búsqueda/actualización recursiva en TODO el árbol (corregido en TSK-0075;
// la asunción anterior de esta cabecera ya no es cierta y quedaba
// documentada aquí como referencia histórica): la asunción original
// ("`canvas-view.js` NUNCA asigna `dataset.nodeId` a un nodo anidado", TSK-
// 0050) quedó obsoleta con TSK-0072, que SÍ asigna `dataset.nodeId`
// recursivamente a cualquier bloque anidado dentro de `rama_si`/
// `rama_sino`/`cuerpo` (`crearItemBloque`, canvas-view.js). Por eso
// `idSeleccionadoEnCanvas` puede coincidir con una sentencia a CUALQUIER
// profundidad del árbol, no solo con una directa de
// `programa.cuerpo.sentencias`. `buscarSentencia` usa `buscarNodoPorId` de
// `editor-model.js` (recorrido recursivo ya existente, sin reimplementarlo)
// para encontrar el nodo esté donde esté. `actualizarSentencia` reconstruye
// de forma inmutable el camino completo desde la raíz (`programaActual`)
// hasta el nodo editado -- cada ancestro en ese camino se copia (`{
// ...nodo }` más el array/nodo hijo modificado reconstruido con `slice`/
// nuevo objeto), y el resto del árbol (ramas no afectadas) se comparte por
// referencia sin copiar, mismo idioma inmutable ya usado en el resto del
// módulo (ARC-R1).
//
// Campos editables de esta tarea (criterio de aceptación de TSK-0050):
// `Entrada.variable`, `Asignacion.variable`, `Comentario.texto`,
// `Llamada.nombre_funcion` (con `<input>` nativo; los 3 primeros —salvo
// `texto`, que es texto libre— validan formato de identificador) y
// `Asignacion.expresion`, `Salida.expresion`, `Condicional.condicion`
// (reutilizando `expression-editor.js`).
//
// Ampliación TSK-0067 ("Edición de la condición del Bucle"): se añaden
// `BucleMientras.condicion` y `RepetirHasta.condicion` a `CAMPOS_POR_TIPO`
// con el mismo patrón de campo `expresion` que `Condicional.condicion`
// (reutilizando `expression-editor.js` tal cual, sin reimplementarlo).
// Verificado en `editor-model.js` (`crearBucleMientras`/`crearRepetirHasta`):
// ambos constructores exponen un único campo `condicion: NodoModelo`,
// exactamente la misma forma que `Condicional.condicion`, por lo que el
// mismo `crearCampoExpresion` sirve sin cambios. `BucleFinPara` (el otro
// constructor de bucle real en `editor-model.js`) quedó fuera de esa tarea
// por tener un contador con 4 campos propios; desde DEC-129 se edita con
// cuatro filas de la misma tabla (la variable como texto-identificador y
// `inicio`/`fin`/`paso` como expresiones), que ya admitía varios campos.
//
// AMBIGÜEDAD NO RESUELTA EN SILENCIO (repórtese también en el historial de
// la tarea): ¿qué mostrar cuando el bloque seleccionado no tiene NINGÚN
// campo editable en este panel? El canvas actual nunca coloca Inicio/Fin/
// Conector (no tienen nodo IR, `colocarBloque(null, ...)` no inserta nada,
// ver cabecera de `canvas-view.js`), pero SÍ puede colocar otros nodos de
// sentencia con nodo IR propio que esta tarea no menciona como editables
// (p.ej. `Declaracion`). Se decide: cualquier tipo
// de nodo que no esté en `CAMPOS_POR_TIPO` (la tabla explícita de los campos
// cubiertos por TSK-0050 y TSK-0067) muestra un mensaje explícito
// ("...no tiene contenido editable en este panel") en vez de fallar en
// silencio o mostrar un formulario vacío indistinguible de un error.
//
// Resolución de `variablesDisponibles` para `expression-editor.js` (la
// misma ambigüedad que ese módulo dejó documentada y delegada "al panel que
// lo integre, TSK-0050/0051, que sí conoce el `Programa` completo"): se
// recorre el `Programa` completo (con `recorrerNodos`, sin reimplementar
// ese recorrido) reuniendo los nombres de `Entrada.variable`,
// `Asignacion.variable`, `BucleFinPara.variable`, `Declaracion.nombre` y
// `Parametro.nombre`. Es solo una sugerencia de autocompletado
// (expression-editor.js ya la trata como tal, nunca como lista cerrada).
//
// Inmutabilidad y ARC-R1 (docs/03-frontend.md, roadmap §7): cada edición
// reconstruye `programaActual` con el mismo idioma ya usado en
// `block-operations.js` (`{ ...programaActual, cuerpo: nuevaSecuencia }`,
// arrays reconstruidos con `slice`/copia, nunca mutación in place) y
// notifica por `onCambioPrograma`. Este módulo NUNCA importa ni toca
// `editor-visual-state.js`: no existe ninguna ruta de código aquí que
// pueda cambiar una posición/tamaño visual.
//
// Estabilidad del foco mientras se escribe (03-frontend.md §13.1 "Foco
// visible, no oculto"): a diferencia de `block-operations.js` (cuyos
// controles son solo botones y puede permitirse reconstruir toda la lista
// tras cada acción), aquí los campos son `<input>` de texto en los que el
// usuario está escribiendo. Reconstruir el DOM en cada pulsación robaría el
// foco y la posición del cursor. Por eso el cuerpo de campos
// (`cuerpoCampos`) SOLO se reconstruye cuando cambia el bloque seleccionado
// (o al montar); una edición de campo actualiza `programaActual` y notifica
// sin volver a construir el DOM de ese campo. `expression-editor.js` sigue
// el mismo principio de forma nativa (gestiona su propio DOM interno sin
// pedir que lo remonten).
//
// TSK-0068 introdujo aquí un campo transversal `nombreCorto` (identificador
// corto opcional, aplicable a cualquier tipo de nodo). TSK-0213 (petición
// directa del usuario, 2026-07-24) lo elimina por completo: ya no existe
// ningún campo "Nombre corto" en este panel, ni validación ni escritura en
// el modelo. `ir-serialization.js` conserva (con el comentario actualizado
// en ese archivo) el filtro que excluía `nombreCorto` del IR-JSON, ahora
// solo como salvaguarda defensiva frente a datos ya guardados en el
// `localStorage` de un usuario real antes de este cambio -- no porque este
// módulo (ni ningún otro) siga produciendo ese campo.
//
// --------------------------------------------------------------------------
// TSK-0088 (petición directa del usuario): toggle real de apertura/cierre
// por CLIC, además del `focusin` ya existente (que se mantiene sin cambios:
// ver "canvasRoot" arriba -- sigue siendo el mecanismo por el que tabular
// con teclado hasta un bloque muestra su contenido, comportamiento ya
// aprobado y cubierto por tests previos que este cambio no puede romper).
// Se añaden DOS listeners nuevos y adicionales sobre `canvasRoot`,
// `mousedown` y `click` (delegados, mismo patrón de `closest('[data-node-
// id]')` que `manejarFocusInCanvas`), que implementan el criterio de
// aceptación textual: clic en un bloque sin panel abierto lo abre; clic en
// el MISMO bloque con el panel ya abierto lo cierra; clic en OTRO bloque
// cierra el anterior y abre el nuevo (nunca dos a la vez -- ya garantizado
// por construcción: solo existe una `idSeleccionadoEnCanvas` a la vez).
//
// Por qué DOS eventos (`mousedown` + `click`) y no uno: el orden real de
// eventos de un clic de ratón en un navegador es `mousedown` -> `focus`/
// `focusin` (si el elemento pulsado es focalizable, como lo son los
// `<button>` de `canvas-view.js`) -> `mouseup` -> `click`. El propio
// `focusin` YA actualiza `idSeleccionadoEnCanvas` (línea arriba) ANTES de
// que el `click` llegue a dispararse: si `manejarClickCanvas` comparase el
// id pulsado contra `idSeleccionadoEnCanvas` EN ESE MOMENTO, "clic en un
// bloque nuevo" sería indistinguible de "clic en el bloque que acaba de
// abrirse" -- cerraría el panel que se acababa de abrir en el mismo gesto,
// en vez de dejarlo abierto. `manejarMouseDownCanvas` captura, ANTES de que
// el foco se mueva, si el bloque pulsado YA estaba abierto en ese instante;
// `manejarClickCanvas` usa ese valor capturado (no el estado "en vivo") para
// decidir el toggle.
//
// Activación por TECLADO (Enter/Espacio sobre un `<button>` ya enfocado):
// el navegador dispara `click` directamente, sin ningún `mousedown` previo
// (no hay ratón involucrado). En ese caso no existe ninguna carrera de foco
// que compensar -- `manejarClickCanvas` cae a comparar directamente contra
// el estado "en vivo" (`idBloquePulsado` no coincide con el id de este
// evento, porque no hubo `mousedown` para él). El resultado es un toggle
// real y accesible SIN código adicional: `Tab` hasta un bloque ya lo abre
// (vía `focusin`, comportamiento preexistente); Enter/Espacio sobre ese
// mismo bloque ya enfocado lo cierra (coincide con el estado "en vivo", que
// ya está abierto); una segunda pulsación lo vuelve a abrir.
//
// `onSeleccionCambiada` (opción nueva, opcional): notifica CUALQUIER cambio
// de `idSeleccionadoEnCanvas` (abrir, cerrar o cambiar de bloque), tanto si
// lo origina `focusin` como el nuevo toggle de clic como una llamada
// programática a `seleccionarBloque`. Existe porque `editor-controller.js`
// (TSK-0088) necesita recalcular el anclaje/visibilidad de `contenidoSlot`
// en CADA cambio de selección, no solo en `focusin` (que hoy dispara su
// propio recálculo mediante un listener aparte sobre el mismo
// `canvasRoot`): un clic que CIERRA un panel ya abierto sin mover el foco
// (el bloque ya estaba enfocado) no dispara ningún `focusin` nuevo, así que
// depender solo de ese evento dejaría el contenedor visualmente abierto tras
// cerrarlo. Callback puramente informativo (nunca cambia el comportamiento
// de este módulo): se invoca DESPUÉS de renderizar, con el nuevo id (o
// `null`).
//
// Ampliación TSK-0209 (DEC-072, alcance explícitamente acotado por el
// usuario: edición VISUAL de contenido, sin ejecución real de paso de
// parámetros ni de resolución de retorno en el motor de ejecución del
// backend -- eso queda fuera de esta ficha): se añaden dos campos que
// `editor-model.js` ya exponía sin ninguna forma de editarlos desde la UI.
//
//   1. `Retorno.expresion` (`crearRetorno`, puede ser `null`): se añade a
//      `CAMPOS_POR_TIPO` con el mismo tipo `'expresion'` que
//      `Salida.expresion`, reutilizando `crearCampoExpresion` sin ningún
//      control nuevo. Diferencia real frente a `Salida`/`Asignacion`/
//      `Condicional` (cuyas factorías EXIGEN una expresión no nula,
//      `requerirNodo`): `crearRetorno({})` permite `expresion: null` (el
//      bloque "Fin" implícito de una función sin valor de retorno, y el
//      caso real de diagramas ya guardados por alumnos con el antiguo
//      bloque "Fin").
//
//      CORRECCIÓN (rechazo real de architecture-reviewer, TSK-0209, segunda
//      ronda -- ver historial de la tarea): una primera versión de este
//      campo, al ver `expresion === null`, la "materializaba" YA (antes de
//      montar el widget, con solo seleccionar/tabular hasta el bloque) a un
//      literal entero 0 real y la ESCRIBÍA en el modelo con
//      `actualizarSentencia`, notificando `onCambioPrograma`. Eso era una
//      escritura real de datos disparada por una acción de SOLO LECTURA
//      (ver/inspeccionar el bloque): `Retorno.expresion = null` ("no
//      devuelve valor") y `Retorno.expresion = Literal(0)` ("devuelve el
//      valor 0") son ejecuciones DISTINTAS para
//      `backend/src/app/domain/diagram_engine.py`, así que convertir la
//      primera en la segunda solo por seleccionar el bloque cambiaba el
//      programa del alumno sin que él lo pidiera. Corregido: `null` se pasa
//      TAL CUAL a `crearCampoExpresion`/`expression-editor.js`, que ya lo
//      soporta de forma nativa (`determinarModoInicial` arranca en el primer
//      modo permitido, "Valor fijo", con un `<input>` que muestra "0" por
//      defecto) -- el widget ya muestra un valor inicial coherente SIN que
//      el modelo tenga que guardar nada. Igual que el resto de campos
//      `'expresion'` de este panel, la escritura real en `programaActual`
//      solo ocurre cuando el alumno interactúa de verdad con el control
//      (`crearEditorExpresion`/`construirSelectorExpresion` nunca notifica
//      `onCambio` en su propio montaje, solo ante eventos reales `input`/
//      `change`). Placeholder colocado por `palette-view.js` (bloque
//      "Retorno" de la paleta): siempre trae `expresion: literalCero()`,
//      nunca `null` -- el caso `expresion: null` solo se da de verdad con un
//      `Retorno` sin valor (equivalente al antiguo bloque "Fin") construido
//      a mano o deserializado de un IR-JSON previo a esta tarea, y ahora
//      sigue siendo `null` hasta que el alumno edite ese campo de verdad.
//   2. `Llamada.argumentos` (`crearLlamada`, `NodoModelo[]`, lista de
//      EXPRESIONES -- nunca de nombres, por eso NUNCA se valida con
//      `PATRON_IDENTIFICADOR`, a diferencia de `nombre_funcion`): nuevo tipo
//      de campo `'lista-expresiones'` (`crearCampoListaExpresiones`, más
//      abajo) que monta un `crearCampoExpresion` por argumento y ofrece
//      "Añadir argumento"/"Quitar argumento N". No existía ningún patrón de
//      "lista editable" reutilizable en el resto del editor (se revisó
//      `block-operations.js` y `editor-controller.js#renderizarPestanas-
//      Diagramas`: ambos reconstruyen una lista completa de BOTONES tras
//      cada cambio, nunca una lista de sub-widgets con estado propio como
//      `expression-editor.js`); sí se reutiliza de ambos el CRITERIO de
//      gestión de foco tras eliminar un elemento de una lista (nunca caer a
//      `document.body`: `pedirConfirmacionEliminarFuncion`/
//      `eliminarFuncionDiagrama` mueven el foco a un control estable
//      conocido) -- aquí, tras "Quitar", el foco se mueve al botón "Añadir
//      argumento" (control estable, nunca se destruye); tras "Añadir", al
//      botón "Quitar" del argumento recién creado (el control lógico
//      siguiente, igual que `mostrarFormularioNuevaFuncion` mueve el foco al
//      primer campo del formulario recién mostrado).
//
//      A diferencia del resto de campos de este panel (que NUNCA
//      reconstruyen su propio DOM tras montarlo, ver "Estabilidad del foco"
//      más abajo), el campo de lista SÍ reconstruye su propio sub-DOM al
//      añadir/quitar un argumento (el NÚMERO de controles cambia, no hay
//      forma de evitarlo) -- pero esa reconstrucción es LOCAL a
//      `crearCampoListaExpresiones` (nunca llama a `renderizarCampos()` del
//      panel completo), así que el resto de campos del bloque seleccionado
//      (p.ej. `nombre_funcion`) conserva su foco/cursor intacto.
//
//      `reemplazarNodoPorId` (más abajo) NO necesitó ningún cambio: tanto
//      editar `Retorno.expresion` (real, del alumno) como "editar/añadir/
//      quitar" un argumento de `Llamada.argumentos` reemplazan de una vez el
//      campo
//      COMPLETO (`expresion` o `argumentos`) del nodo ya localizado por SU
//      PROPIO id (`idNodoSeleccionado`, el de la `Llamada`/`Retorno`, nunca
//      el id interno de un argumento individual) -- exactamente el mismo
//      patrón "reemplaza el valor de un campo escalar" que ya usan
//      `Asignacion.expresion`/`Condicional.condicion` (una expresión
//      generada por `expression-editor.js` es simplemente un valor NUEVO
//      para ese campo, sea ese valor un nodo suelto o -- como aquí -- un
//      array de nodos reconstruido de forma inmutable con `slice`/`concat`,
//      nunca mutado in place).
//
// Ampliación TSK-0212 (DEC-074, petición directa del usuario): `Entrada.tipo`,
// campo NUEVO y opcional en el esquema (a diferencia de TSK-0209/TSK-0211,
// que solo poblaban campos YA EXISTENTES -- este es un campo nuevo tanto en
// `editor-model.js#crearEntrada` como en `backend/src/app/domain/ir.py`,
// ambos en rutas_permitidas de esta ficha en concreto). Se añade a
// `CAMPOS_POR_TIPO[TIPO_NODO.ENTRADA]` un segundo campo, de tipo NUEVO
// `'tipo-primitivo-opcional'` (`crearCampoTipoPrimitivoOpcional`, más abajo):
// un `<select>` con el mismo enum `TIPO_PRIMITIVO`/mismo patrón visual ya
// usado para el selector de tipo de `Parametro` (TSK-0211,
// `editor-controller.js#crearSelectorTipoParametro`), más una opción extra
// "Sin declarar" que aplica `null` -- a diferencia de `Parametro.tipo`
// (requerido por el esquema), este campo es opcional de verdad: un
// diagrama ya existente sin este campo, o un alumno que decide no
// declararlo, deben poder seguir así sin ningún error. El valor declarado
// (si lo hay) lo consume `execution-panel.js` para validar el valor
// introducido por el alumno ANTES de enviarlo a ejecutar (ver cabecera de
// ese módulo); `diagram_engine.py` no cambia de comportamiento.
//
// Ampliación TSK-0216 (DEC-075, continuación de TSK-0215/backend): se añade a
// `CAMPOS_POR_TIPO[TIPO_NODO.LLAMADA]` un tercer campo, "Guardar el valor
// devuelto en la variable (opcional)" (`variable_destino`, campo NUEVO en
// `editor-model.js#crearLlamada`, mismo precedente aditivo/retrocompatible
// que `Entrada.tipo`/TSK-0212). A diferencia de `Entrada.tipo` (un `<select>`
// sobre un enum cerrado), aquí el control natural es un `<input>` de TEXTO
// (el alumno escribe el NOMBRE que quiera darle a la variable, no elige de
// una lista cerrada) -- por eso NO se reutiliza `crearCampoTipoPrimitivoOpcional`,
// sino que se reutiliza `crearCampoTexto` (sin ningún cambio en esa función)
// con un validador nuevo, `validarIdentificadorOpcional` (más abajo): a
// diferencia de `validarIdentificador` (usado por `Entrada.variable`/
// `Llamada.nombre_funcion`, que rechazan la cadena vacía), este acepta
// explícitamente la cadena vacía como válida -- es el sentinel "no capturar
// el retorno" (mismo significado que "Sin declarar" en el selector de
// `Entrada.tipo`: vacío = comportamiento actual, retorno descartado). La
// traducción "cadena vacía -> `null`" ocurre en el `aplicar` de la nueva rama
// `'texto-opcional'` de `renderizarCampos()` (no dentro de `crearCampoTexto`,
// que sigue siendo agnóstico de esta regla de negocio, igual que ya lo era
// para `nombre_funcion`/`variable`/`texto`). Nunca se valida aquí que "la
// función sea un procedimiento sin retorno" ni que "el cuerpo llegue a un
// `Retorno` con expresión" -- esa es responsabilidad exclusiva del motor de
// ejecución (`diagram_engine.py`, TSK-0215); este panel es edición VISUAL de
// contenido, sin ejecución real, mismo alcance ya acotado por DEC-072/
// DEC-074 para el resto de campos de esta ampliación.
//
// --------------------------------------------------------------------------
//
// `canvasRoot` ya no es siempre `canvasVista.elemento` (ver "DECISIÓN 10" en
// la cabecera de `editor-controller.js`): para que este panel pueda
// sobrevivir a una reconstrucción del canvas sin destruirse (necesario para
// no perder el foco/cursor de un `<input>` a mitad de escritura), ese
// controlador le pasa un contenedor ESTABLE (`canvasSlot`) que también
// contiene, como hermano del canvas, al propio `contenidoSlot` -- el
// envoltorio de la `<section>` de ESTE módulo. Por eso `eventoDentroDel-
// PropioPanel` (más abajo) ignora explícitamente cualquier evento
// `focusin`/`mousedown`/`click` cuyo objetivo esté DENTRO de la propia
// `seccion`: sin ese guardián, escribir en un campo propio (sin `data-node-
// id`) burbujearía hasta `canvasRoot` y se interpretaría como "fuera de
// cualquier bloque", cerrando el panel en el que se está escribiendo.

import {
  esNodo,
  TIPO_NODO,
  TIPO_PRIMITIVO,
  crearPrograma,
  recorrerNodos,
  buscarNodoPorId,
  crearLiteral,
} from './editor-model.js';
import { crearEditorExpresion, MODO_PRESENTACION } from './expression-editor.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 */

/** Formato mínimo de identificador: mismo criterio que
 * `expression-editor.js` (constante privada allí, no exportada; se duplica
 * aquí como regex trivial, no como lógica de negocio reutilizable). */
const PATRON_IDENTIFICADOR = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Expresión mínima válida reutilizada como valor por defecto (TSK-0209):
 * mismo criterio y misma factoría real que `palette-view.js#literalCero`
 * (nunca un objeto libre) -- un literal entero 0. Se usa como expresión
 * inicial de un argumento NUEVO añadido a `Llamada.argumentos` mediante
 * "Añadir argumento" (una acción real del alumno, no un montaje/selección:
 * escribir en el modelo ahí es correcto). NO se usa para `Retorno.expresion`
 * cuando llega como `null` -- ver el comentario de esa rama en
 * `renderizarCampos()` (más abajo) para el porqué exacto (hallazgo real de
 * architecture-reviewer, TSK-0209, segunda ronda: materializar en el
 * montaje/selección era una escritura indebida disparada por una acción de
 * solo lectura).
 * @returns {NodoModelo}
 */
function literalPorDefecto() {
  return crearLiteral({ valor: 0, tipo: TIPO_PRIMITIVO.ENTERO });
}

/**
 * Ampliación TSK-0212 (DEC-074): `Entrada.tipo`, campo NUEVO y opcional en el
 * esquema (`editor-model.js#crearEntrada`, `backend/src/app/domain/ir.py`).
 * Reutiliza EXACTAMENTE el mismo enum/patrón visual de selector ya usado por
 * `Parametro.tipo` (TSK-0211, `editor-controller.js#crearSelectorTipoParametro`
 * -- ese componente vive en `editor-controller.js`, fuera de las rutas
 * permitidas de esta ficha, así que aquí se reconstruye el mismo patrón, no
 * se importa; mismo precedente de "duplicar la constante trivial de
 * etiquetas" ya documentado en ese módulo para `ETIQUETA_TIPO_PRIMITIVO_PARAMETRO`,
 * que a su vez cita `expression-editor.js` como origen del patrón). A
 * diferencia de `Parametro.tipo` (REQUERIDO por el esquema del backend), el
 * selector de `Entrada.tipo` SIEMPRE ofrece una opción adicional "Sin
 * declarar" (`VALOR_TIPO_SIN_DECLARAR`, cadena vacía -- nunca colisiona con
 * ningún valor real de `TIPO_PRIMITIVO`, todos no vacíos) que aplica `null`:
 * el campo es opcional de verdad, un diagrama existente sin este campo (o
 * un alumno que decide no declararlo) debe poder seguir así.
 */
const ETIQUETA_TIPO_PRIMITIVO = Object.freeze({
  [TIPO_PRIMITIVO.ENTERO]: 'Entero',
  [TIPO_PRIMITIVO.DECIMAL]: 'Decimal',
  [TIPO_PRIMITIVO.CADENA]: 'Texto',
  [TIPO_PRIMITIVO.BOOLEANO]: 'Booleano',
});

/** Valor de `<option>` reservado para "sin tipo declarado" (ver comentario
 * de `ETIQUETA_TIPO_PRIMITIVO` arriba): cadena vacía, distinta de cualquier
 * valor real de `TIPO_PRIMITIVO` (ninguno es `''`). */
const VALOR_TIPO_SIN_DECLARAR = '';

/**
 * Tabla explícita de campos editables por tipo de nodo (criterio de
 * aceptación de TSK-0050; ampliada por TSK-0067/TSK-0209/TSK-0212). Cualquier
 * tipo de nodo ausente de esta tabla se trata como "sin contenido editable en
 * este panel" (ver ambigüedad documentada en la cabecera del archivo).
 * @type {Record<string, Array<{ tipo: 'texto' | 'expresion' | 'lista-expresiones' | 'tipo-primitivo-opcional' | 'texto-opcional', campo: string, etiqueta: string, identificador?: boolean }>>}
 */
const CAMPOS_POR_TIPO = Object.freeze({
  [TIPO_NODO.ENTRADA]: [
    { tipo: 'texto', campo: 'variable', etiqueta: 'Nombre de la variable', identificador: true },
    { tipo: 'tipo-primitivo-opcional', campo: 'tipo', etiqueta: 'Tipo esperado (opcional)' },
  ],
  [TIPO_NODO.ASIGNACION]: [
    { tipo: 'texto', campo: 'variable', etiqueta: 'Nombre de la variable', identificador: true },
    { tipo: 'expresion', campo: 'expresion', etiqueta: 'Valor asignado (expresión)' },
  ],
  [TIPO_NODO.COMENTARIO]: [{ tipo: 'texto', campo: 'texto', etiqueta: 'Texto del comentario' }],
  [TIPO_NODO.LLAMADA]: [
    { tipo: 'texto', campo: 'nombre_funcion', etiqueta: 'Nombre de la función o procedimiento', identificador: true },
    { tipo: 'lista-expresiones', campo: 'argumentos', etiqueta: 'Argumentos' },
    {
      tipo: 'texto-opcional',
      campo: 'variable_destino',
      etiqueta: 'Guardar el valor devuelto en la variable (opcional)',
    },
  ],
  [TIPO_NODO.SALIDA]: [{ tipo: 'expresion', campo: 'expresion', etiqueta: 'Expresión mostrada' }],
  [TIPO_NODO.CONDICIONAL]: [{ tipo: 'expresion', campo: 'condicion', etiqueta: 'Condición' }],
  [TIPO_NODO.BUCLE_MIENTRAS]: [{ tipo: 'expresion', campo: 'condicion', etiqueta: 'Condición del bucle' }],
  // DEC-129: el bucle PARA se edita aquí (antes «no tiene contenido
  // editable»). Las etiquetas concuerdan con su aviso: los campos de texto
  // anuncian «<etiqueta> actualizado a …» y los de expresión «<etiqueta>
  // actualizada.».
  [TIPO_NODO.BUCLE_FIN_PARA]: [
    { tipo: 'texto', campo: 'variable', etiqueta: 'Nombre de la variable que cuenta', identificador: true },
    { tipo: 'expresion', campo: 'inicio', etiqueta: 'Expresión inicial (desde dónde cuenta)' },
    { tipo: 'expresion', campo: 'fin', etiqueta: 'Expresión final (hasta dónde cuenta, incluida)' },
    { tipo: 'expresion', campo: 'paso', etiqueta: 'Expresión del paso (de cuánto en cuánto)' },
  ],
  [TIPO_NODO.REPETIR_HASTA]: [{ tipo: 'expresion', campo: 'condicion', etiqueta: 'Condición del bucle' }],
  [TIPO_NODO.RETORNO]: [{ tipo: 'expresion', campo: 'expresion', etiqueta: 'Expresión devuelta' }],
});

let contadorInstanciasPanel = 0;

/**
 * @param {unknown} valor
 * @returns {NodoModelo}
 */
function requerirPrograma(valor) {
  if (!esNodo(valor) || valor.nodo !== TIPO_NODO.PROGRAMA) {
    throw new TypeError('crearPanelContenidoBloque requiere "programa": un nodo Programa del modelo del editor');
  }
  const cuerpo = /** @type {Record<string, unknown>} */ (valor).cuerpo;
  if (!esNodo(cuerpo) || cuerpo.nodo !== TIPO_NODO.SECUENCIA) {
    throw new TypeError('el "programa" recibido debe tener un "cuerpo" de tipo Secuencia');
  }
  return valor;
}

/**
 * Reconstruye `nodo` de forma inmutable reemplazando, a cualquier
 * profundidad de su subárbol, el (único) nodo cuyo `id` coincide con `id`
 * por el resultado de `actualizar(nodoEncontrado)`. Mismo patrón de
 * recorrido que `recorrerNodos`/`buscarNodoPorId` de `editor-model.js`
 * (valores que son nodo, y arrays de valores que pueden contener nodos),
 * pero reconstruyendo en vez de solo iterar: cada ancestro en el camino
 * hasta el nodo encontrado se copia (`{ ...nodo }`, arrays con `map`/nuevo
 * array), y cualquier rama del árbol NO afectada se devuelve tal cual (misma
 * referencia, sin copiar) -- estructura compartida, mismo idioma inmutable
 * que el resto del módulo (ARC-R1). Si no se encuentra ningún nodo con ese
 * `id`, devuelve `nodo` sin cambios (misma referencia), lo que permite a la
 * llamada distinguir "no encontrado" comparando por referencia.
 * @param {NodoModelo} nodo
 * @param {string} id
 * @param {(encontrado: NodoModelo) => NodoModelo} actualizar
 * @returns {NodoModelo}
 */
function reemplazarNodoPorId(nodo, id, actualizar) {
  if (nodo.id === id) return actualizar(nodo);

  let huboCambio = false;
  /** @type {Record<string, unknown>} */
  const nuevoNodo = { ...nodo };

  for (const [clave, valor] of Object.entries(nodo)) {
    if (esNodo(valor)) {
      const nuevoValor = reemplazarNodoPorId(valor, id, actualizar);
      if (nuevoValor !== valor) {
        nuevoNodo[clave] = nuevoValor;
        huboCambio = true;
      }
      continue;
    }
    if (Array.isArray(valor)) {
      let arrayCambiado = false;
      const nuevoArray = valor.map((elemento) => {
        if (!esNodo(elemento)) return elemento;
        const nuevoElemento = reemplazarNodoPorId(elemento, id, actualizar);
        if (nuevoElemento !== elemento) arrayCambiado = true;
        return nuevoElemento;
      });
      if (arrayCambiado) {
        nuevoNodo[clave] = nuevoArray;
        huboCambio = true;
      }
    }
  }

  return huboCambio ? /** @type {NodoModelo} */ (nuevoNodo) : nodo;
}

/**
 * Reúne nombres de variable ya declarados en TODO el programa (no solo el
 * nivel superior: una variable declarada dentro de un bucle sigue siendo
 * una sugerencia útil de autocompletado para una expresión de nivel
 * superior). Ver "Resolución de variablesDisponibles" en la cabecera.
 * @param {NodoModelo} programa
 * @returns {string[]}
 */
function obtenerVariablesDeclaradas(programa) {
  const nombres = new Set();
  for (const nodo of recorrerNodos(programa)) {
    if (
      (nodo.nodo === TIPO_NODO.ENTRADA || nodo.nodo === TIPO_NODO.ASIGNACION || nodo.nodo === TIPO_NODO.BUCLE_FIN_PARA) &&
      typeof nodo.variable === 'string'
    ) {
      nombres.add(nodo.variable);
    }
    if (
      (nodo.nodo === TIPO_NODO.DECLARACION || nodo.nodo === TIPO_NODO.PARAMETRO) &&
      typeof nodo.nombre === 'string'
    ) {
      nombres.add(nodo.nombre);
    }
  }
  return Array.from(nombres).sort();
}

/**
 * @param {string} crudo
 * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
 */
function validarIdentificador(crudo) {
  const texto = crudo.trim();
  if (!PATRON_IDENTIFICADOR.test(texto)) {
    return {
      valido: false,
      mensaje: 'Escribe un identificador válido: empieza por una letra o "_", sin espacios ni símbolos.',
    };
  }
  return { valido: true, valor: texto };
}

/**
 * Texto libre (p.ej. `Comentario.texto`): sin validación de formato de
 * identificador (no aplica, ver criterio de aceptación), solo se rechaza
 * vacío para no producir un nodo inconsistente con lo que exige la propia
 * factoría `crearComentario` de editor-model.js (`texto` no vacío).
 * @param {string} crudo
 * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
 */
function validarTextoLibre(crudo) {
  if (crudo.trim().length === 0) {
    return { valido: false, mensaje: 'Este campo no puede quedar vacío.' };
  }
  return { valido: true, valor: crudo };
}

/**
 * Identificador OPCIONAL (TSK-0216, DEC-075): usado por
 * `Llamada.variable_destino`. A diferencia de `validarIdentificador` (que
 * rechaza la cadena vacía), aquí la cadena vacía es un valor VÁLIDO -- es el
 * sentinel "no capturar el valor devuelto" (mismo significado que "Sin
 * declarar" en `crearCampoTipoPrimitivoOpcional`). La cadena vacía validada
 * aquí se traduce a `null` en el `aplicar` de la rama `'texto-opcional'` de
 * `renderizarCampos()` (más abajo), no en esta función: `validarIdentificadorOpcional`
 * solo decide si el TEXTO en sí es aceptable (vacío, o un identificador con
 * el mismo formato que el resto del panel), nunca qué valor final se guarda
 * en el modelo.
 * @param {string} crudo
 * @returns {{ valido: true, valor: string } | { valido: false, mensaje: string }}
 */
function validarIdentificadorOpcional(crudo) {
  if (crudo.trim().length === 0) {
    return { valido: true, valor: '' };
  }
  return validarIdentificador(crudo);
}

/**
 * @param {HTMLElement} contenedor
 * @param {string} mensaje
 * @param {string} idMensaje
 * @param {HTMLElement} control
 */
function establecerMensajeError(contenedor, mensaje, idMensaje, control) {
  contenedor.textContent = mensaje;
  if (mensaje) {
    control.setAttribute('aria-describedby', idMensaje);
    control.setAttribute('aria-invalid', 'true');
  } else {
    control.removeAttribute('aria-describedby');
    control.removeAttribute('aria-invalid');
  }
}

/**
 * Construye un campo de texto nativo accesible (label + input + mensaje de
 * error `role="alert"`, mismo patrón que `expression-editor.js`). Valida en
 * cada `input`; solo aplica el cambio (`aplicar`) cuando el valor es válido,
 * dejando el último valor válido intacto mientras el campo esté en un
 * estado inválido (igual que `expression-editor.js`).
 * @param {{
 *   idBase: string,
 *   etiqueta: string,
 *   valorInicial: string,
 *   validar: (crudo: string) => { valido: true, valor: string } | { valido: false, mensaje: string },
 *   aplicar: (valor: string) => void,
 * }} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
function crearCampoTexto({ idBase, etiqueta, valorInicial, validar, aplicar }) {
  const contenedor = document.createElement('div');
  contenedor.className = 'block-content-panel-campo';

  const idInput = `${idBase}-input`;
  const label = document.createElement('label');
  label.setAttribute('for', idInput);
  label.textContent = etiqueta;

  const input = document.createElement('input');
  input.type = 'text';
  input.id = idInput;
  input.autocomplete = 'off';
  input.value = valorInicial;

  const idError = `${idBase}-error`;
  const error = document.createElement('p');
  error.className = 'block-content-panel-error';
  error.id = idError;
  error.setAttribute('role', 'alert');

  contenedor.append(label, input, error);

  function manejarInput() {
    const resultado = validar(input.value);
    if (!resultado.valido) {
      establecerMensajeError(error, resultado.mensaje, idError, input);
      return;
    }
    establecerMensajeError(error, '', idError, input);
    aplicar(resultado.valor);
  }
  input.addEventListener('input', manejarInput);

  return {
    elemento: contenedor,
    destruir: () => input.removeEventListener('input', manejarInput),
  };
}

/**
 * Monta una instancia de `expression-editor.js` (TSK-0049, reutilizada tal
 * cual, sin reimplementar su lógica) para un campo de tipo expresión
 * (`Asignacion.expresion`, `Salida.expresion`, `Condicional.condicion`).
 * `modoPresentacion`/`contextoDidactico` (opcionales) se reenvían tal cual a
 * `crearEditorExpresion`; por defecto `'tecnico'`/`null` → el editor de
 * siempre, sin cambios. Solo el campo `condicion` de la Decisión (Condicional)
 * los usa para activar la vista guiada (ver `renderizarCampos`).
 * @param {{
 *   idBase: string,
 *   etiqueta: string,
 *   expresionInicial: NodoModelo | null,
 *   variablesDisponibles: string[],
 *   aplicar: (expresion: NodoModelo) => void,
 *   modoPresentacion?: 'tecnico' | 'guiado',
 *   contextoDidactico?: string | null,
 * }} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
function crearCampoExpresion({
  idBase,
  etiqueta,
  expresionInicial,
  variablesDisponibles,
  aplicar,
  modoPresentacion = MODO_PRESENTACION.TECNICO,
  contextoDidactico = null,
}) {
  const contenedor = document.createElement('div');
  contenedor.className = 'block-content-panel-campo block-content-panel-campo-expresion';

  const subtitulo = document.createElement('h3');
  subtitulo.className = 'block-content-panel-subtitulo';
  subtitulo.id = `${idBase}-titulo`;
  subtitulo.textContent = etiqueta;
  contenedor.append(subtitulo);

  const widget = crearEditorExpresion({
    expresionInicial,
    variablesDisponibles,
    onCambio: aplicar,
    modoPresentacion,
    contextoDidactico,
  });
  contenedor.append(widget.elemento);

  return { elemento: contenedor, destruir: widget.destruir };
}

/**
 * Campo de tipo primitivo OPCIONAL (TSK-0212, DEC-074): usado por
 * `Entrada.tipo`. Mismo enum/patrón visual que el selector de `Parametro.tipo`
 * (TSK-0211, ver comentario de `ETIQUETA_TIPO_PRIMITIVO` más arriba), pero
 * con una opción adicional "Sin declarar" (`VALOR_TIPO_SIN_DECLARAR`) que
 * aplica `null` -- a diferencia de `Parametro.tipo` (requerido por el
 * esquema del backend), este campo es opcional de verdad. Nunca reconstruye
 * su propio DOM tras montarlo (un único `<select>`, igual que `crearCampoTexto`
 * gestiona un único `<input>`): cambiar la selección solo dispara `aplicar`.
 * @param {{
 *   idBase: string,
 *   etiqueta: string,
 *   valorInicial: string | null,
 *   aplicar: (tipo: string | null) => void,
 * }} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
function crearCampoTipoPrimitivoOpcional({ idBase, etiqueta, valorInicial, aplicar }) {
  const contenedor = document.createElement('div');
  contenedor.className = 'block-content-panel-campo';

  const idSelect = `${idBase}-select`;
  const label = document.createElement('label');
  label.setAttribute('for', idSelect);
  label.textContent = etiqueta;

  const select = document.createElement('select');
  select.id = idSelect;

  const opcionSinDeclarar = document.createElement('option');
  opcionSinDeclarar.value = VALOR_TIPO_SIN_DECLARAR;
  opcionSinDeclarar.textContent = 'Sin declarar';
  select.append(opcionSinDeclarar);

  for (const tipo of Object.values(TIPO_PRIMITIVO)) {
    const opcion = document.createElement('option');
    opcion.value = tipo;
    opcion.textContent = ETIQUETA_TIPO_PRIMITIVO[tipo];
    select.append(opcion);
  }

  select.value =
    typeof valorInicial === 'string' && Object.values(TIPO_PRIMITIVO).includes(/** @type {any} */ (valorInicial))
      ? valorInicial
      : VALOR_TIPO_SIN_DECLARAR;

  contenedor.append(label, select);

  function manejarChange() {
    aplicar(select.value === VALOR_TIPO_SIN_DECLARAR ? null : select.value);
  }
  select.addEventListener('change', manejarChange);

  return {
    elemento: contenedor,
    destruir: () => select.removeEventListener('change', manejarChange),
  };
}

/**
 * Campo de LISTA de expresiones (TSK-0209): usado por `Llamada.argumentos`
 * (ver "Ampliación TSK-0209" en la cabecera del archivo para el
 * razonamiento completo). Monta una instancia de `crearCampoExpresion` por
 * argumento (mismo `expression-editor.js` reutilizado, nunca reimplementado)
 * y añade los controles "Añadir argumento"/"Quitar argumento N" para
 * cambiar el TAMAÑO de la lista.
 *
 * A diferencia del resto de campos de este panel (que nunca reconstruyen su
 * propio DOM tras montarlo), este campo SÍ reconstruye su sub-DOM interno al
 * añadir/quitar un argumento -- inevitable, cambia el número de controles --
 * pero esa reconstrucción es LOCAL (nunca dispara `renderizarCampos()` del
 * panel completo), así que el resto de campos del bloque seleccionado
 * conserva su foco/cursor intacto.
 * @param {{
 *   idBase: string,
 *   etiqueta: string,
 *   argumentosIniciales: NodoModelo[],
 *   variablesDisponibles: string[],
 *   aplicarLista: (argumentos: NodoModelo[]) => void,
 *   anunciar: (mensaje: string) => void,
 * }} opciones
 * @returns {{ elemento: HTMLElement, destruir: () => void }}
 */
function crearCampoListaExpresiones({ idBase, etiqueta, argumentosIniciales, variablesDisponibles, aplicarLista, anunciar }) {
  const contenedor = document.createElement('div');
  contenedor.className = 'block-content-panel-campo block-content-panel-campo-lista';

  const subtitulo = document.createElement('h3');
  subtitulo.className = 'block-content-panel-subtitulo';
  subtitulo.textContent = etiqueta;
  contenedor.append(subtitulo);

  const listaElemento = document.createElement('div');
  listaElemento.className = 'block-content-panel-lista-argumentos';

  const botonAnadir = document.createElement('button');
  botonAnadir.type = 'button';
  botonAnadir.className = 'block-content-panel-boton-lista';
  botonAnadir.textContent = 'Añadir argumento';

  contenedor.append(listaElemento, botonAnadir);

  /** Estado local mutable de esta instancia: nunca muta el array recibido
   * (se copia superficialmente al montar y en cada operación de
   * añadir/quitar; cada expresión individual sigue siendo inmutable, igual
   * que el resto del módulo). */
  let argumentos = argumentosIniciales.slice();
  /** @type {Array<{ destruirCampo: () => void, botonQuitar: HTMLButtonElement }>} */
  let filas = [];

  /** @param {number} indice */
  function quitarArgumento(indice) {
    argumentos = argumentos.slice(0, indice).concat(argumentos.slice(indice + 1));
    renderizarLista();
    aplicarLista(argumentos.slice());
    anunciar(`Argumento ${indice + 1} quitado.`);
    // Foco gestionado (03-frontend.md §13.1): nunca cae a document.body tras
    // eliminar un elemento de la lista (mismo criterio que
    // `editor-controller.js#eliminarFuncionDiagrama`, que mueve el foco a un
    // control estable conocido). "Añadir argumento" nunca se destruye, es un
    // destino de foco siempre disponible.
    botonAnadir.focus();
  }

  function renderizarLista() {
    for (const fila of filas) fila.destruirCampo();
    filas = [];
    listaElemento.replaceChildren();

    if (argumentos.length === 0) {
      const vacio = document.createElement('p');
      vacio.className = 'block-content-panel-lista-vacia';
      vacio.textContent = 'Sin argumentos todavía.';
      listaElemento.append(vacio);
    }

    argumentos.forEach((argumento, indice) => {
      const fila = document.createElement('div');
      fila.className = 'block-content-panel-argumento';

      const campoExpresion = crearCampoExpresion({
        idBase: `${idBase}-${indice}`,
        etiqueta: `Argumento ${indice + 1}`,
        expresionInicial: argumento,
        variablesDisponibles,
        aplicar: (nuevaExpresion) => {
          argumentos[indice] = nuevaExpresion;
          aplicarLista(argumentos.slice());
          anunciar(`Argumento ${indice + 1} actualizado.`);
        },
      });

      const botonQuitar = document.createElement('button');
      botonQuitar.type = 'button';
      botonQuitar.className = 'block-content-panel-boton-lista block-content-panel-boton-lista-peligro';
      botonQuitar.textContent = `Quitar argumento ${indice + 1}`;
      const manejarQuitar = () => quitarArgumento(indice);
      botonQuitar.addEventListener('click', manejarQuitar);

      fila.append(campoExpresion.elemento, botonQuitar);
      listaElemento.append(fila);

      filas.push({
        destruirCampo: () => {
          campoExpresion.destruir();
          botonQuitar.removeEventListener('click', manejarQuitar);
        },
        botonQuitar,
      });
    });
  }

  function anadirArgumento() {
    argumentos = argumentos.concat([literalPorDefecto()]);
    renderizarLista();
    aplicarLista(argumentos.slice());
    anunciar(`Argumento ${argumentos.length} añadido.`);
    // Foco gestionado: se mueve al botón "Quitar" del argumento recién
    // creado (el control lógico siguiente, mismo criterio que
    // `mostrarFormularioNuevaFuncion`, que mueve el foco al primer campo del
    // formulario recién mostrado) -- nunca cae a document.body.
    filas[filas.length - 1]?.botonQuitar.focus();
  }

  botonAnadir.addEventListener('click', anadirArgumento);
  renderizarLista();

  return {
    elemento: contenedor,
    destruir: () => {
      for (const fila of filas) fila.destruirCampo();
      botonAnadir.removeEventListener('click', anadirArgumento);
    },
  };
}

/**
 * Crea el panel de contenido del bloque seleccionado.
 *
 * @param {{
 *   root?: HTMLElement,
 *   programa?: NodoModelo,
 *   canvasRoot?: HTMLElement,
 *   obtenerEtiqueta?: (nodo: NodoModelo) => string,
 *   onCambioPrograma?: (programa: NodoModelo) => void,
 *   onSeleccionCambiada?: (id: string | null) => void,
 * }} [opciones]
 * @returns {{
 *   elemento: HTMLElement,
 *   obtenerPrograma: () => NodoModelo,
 *   obtenerIdSeleccionadoEnCanvas: () => string | null,
 *   seleccionarBloque: (id: string | null) => void,
 *   destruir: () => void,
 * }}
 */
export function crearPanelContenidoBloque(opciones = {}) {
  const programaInicial = requerirPrograma(opciones.programa ?? crearPrograma());
  const canvasRoot = opciones.canvasRoot ?? null;
  const obtenerEtiqueta =
    typeof opciones.obtenerEtiqueta === 'function' ? opciones.obtenerEtiqueta : (/** @type {NodoModelo} */ nodo) => nodo.nodo;
  const notificarCambioPrograma =
    typeof opciones.onCambioPrograma === 'function' ? opciones.onCambioPrograma : null;
  /** TSK-0088: ver cabecera del archivo ("onSeleccionCambiada"). */
  const notificarSeleccionCambiada =
    typeof opciones.onSeleccionCambiada === 'function' ? opciones.onSeleccionCambiada : null;

  let programaActual = programaInicial;
  /** @type {string | null} */
  let idSeleccionadoEnCanvas = null;
  /** @type {Array<() => void>} */
  let destructoresCamposActuales = [];

  contadorInstanciasPanel += 1;
  const idTitulo = `block-content-panel-heading-${contadorInstanciasPanel}`;

  const seccion = document.createElement('section');
  seccion.className = 'block-content-panel';
  seccion.setAttribute('aria-labelledby', idTitulo);

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'block-content-panel-heading';
  titulo.textContent = 'Contenido del bloque seleccionado';

  const ayuda = document.createElement('p');
  ayuda.className = 'block-content-panel-hint';
  ayuda.textContent =
    'Selecciona un bloque del área de trabajo (con el puntero o con Tab) para ' +
    'ver y editar aquí su contenido real.';

  const cuerpoCampos = document.createElement('div');
  cuerpoCampos.className = 'block-content-panel-body';

  /** Live region moderada (mismo patrón que block-operations.js/
   * canvas-view.js: rol nativo `status`, aria-live="polite"/
   * aria-atomic="true" explícitos por robustez). */
  const estado = document.createElement('p');
  estado.className = 'block-content-panel-status';
  estado.setAttribute('role', 'status');
  estado.setAttribute('aria-live', 'polite');
  estado.setAttribute('aria-atomic', 'true');
  estado.textContent = 'Ningún bloque seleccionado.';

  seccion.append(titulo, ayuda, cuerpoCampos, estado);

  /** @param {string} mensaje */
  function anunciar(mensaje) {
    estado.textContent = mensaje;
  }

  /**
   * Busca el nodo con `id` a cualquier profundidad del árbol (TSK-0075:
   * ya no solo `programa.cuerpo.sentencias` de nivel superior, ver cabecera
   * del archivo). Reutiliza `buscarNodoPorId` de `editor-model.js` (mismo
   * recorrido recursivo que ya usa `obtenerVariablesDeclaradas` vía
   * `recorrerNodos`), sin reimplementar la lógica de recorrido.
   * @param {string} id
   * @returns {NodoModelo | undefined}
   */
  function buscarSentencia(id) {
    return buscarNodoPorId(programaActual, id);
  }

  /**
   * Reconstruye de forma inmutable el nodo con `id`, esté donde esté en el
   * árbol (TSK-0075: nivel superior o dentro de cualquier rama/cuerpo
   * anidado, ver cabecera del archivo) usando `reemplazarNodoPorId`. Si no
   * se encuentra el `id` (el bloque fue eliminado de verdad), no hace nada:
   * `reemplazarNodoPorId` devuelve la misma referencia de `programaActual`
   * y aquí se detecta por identidad para no notificar un cambio vacío.
   * @param {string} id
   * @param {(nodo: NodoModelo) => NodoModelo} actualizar
   */
  function actualizarSentencia(id, actualizar) {
    const nuevoPrograma = reemplazarNodoPorId(programaActual, id, actualizar);
    if (nuevoPrograma === programaActual) return;
    programaActual = nuevoPrograma;
    if (notificarCambioPrograma) notificarCambioPrograma(programaActual);
  }

  function limpiarCampos() {
    for (const destructor of destructoresCamposActuales) destructor();
    destructoresCamposActuales = [];
    cuerpoCampos.replaceChildren();
  }

  /** @param {string} texto */
  function mostrarMensaje(texto) {
    const mensaje = document.createElement('p');
    mensaje.className = 'block-content-panel-mensaje';
    mensaje.textContent = texto;
    cuerpoCampos.append(mensaje);
  }

  /** Reconstruye el cuerpo de campos del bloque seleccionado. SOLO se llama
   * cuando cambia la selección (o al montar): editar un campo NO reconstruye
   * este cuerpo, para no robar el foco/cursor de un `<input>` a mitad de
   * escritura (ver cabecera del archivo). DOM seguro
   * (docs/03-frontend.md §8.2): solo `createElement`/`textContent`.
   *
   * Hallazgo P1 de accessibility-reviewer (revisión de TSK-0050, WCAG 2.2
   * SC 4.1.3 "Status Messages"): un usuario de teclado/lector de pantalla
   * que tabula por el canvas nunca mueve el foco a este panel (vive en otra
   * parte de la página); si el panel cambia de contenido por completo al
   * seleccionar un bloque distinto sin anunciarlo por la live region, ese
   * cambio es silencioso e imperceptible para ese usuario. Por eso CADA
   * rama de esta función (no solo los `aplicar` de cada campo, que ya
   * anunciaban las EDICIONES) termina llamando a `anunciar(...)` con un
   * mensaje que describe qué se está mostrando ahora, incluido el caso
   * "sin campos editables" y el caso "el bloque ya no existe", que eran
   * exactamente los escenarios pedidos explícitamente en el encargo. */
  function renderizarCampos() {
    limpiarCampos();

    if (!idSeleccionadoEnCanvas) {
      mostrarMensaje('Selecciona un bloque del área de trabajo para editar su contenido.');
      anunciar('Ningún bloque seleccionado.');
      return;
    }

    const nodo = buscarSentencia(idSeleccionadoEnCanvas);
    if (!nodo) {
      mostrarMensaje('El bloque seleccionado ya no está disponible en el área de trabajo.');
      anunciar('El bloque seleccionado ya no está disponible en el área de trabajo.');
      return;
    }

    const idNodoSeleccionado = nodo.id;

    const definicionCampos = CAMPOS_POR_TIPO[nodo.nodo];
    if (!definicionCampos || definicionCampos.length === 0) {
      const etiqueta = obtenerEtiqueta(nodo);
      mostrarMensaje(`El bloque "${etiqueta}" no tiene contenido editable en este panel.`);
      anunciar(`El bloque "${etiqueta}" no tiene contenido editable en este panel.`);
      return;
    }

    const variablesDisponibles = obtenerVariablesDeclaradas(programaActual);

    definicionCampos.forEach((definicion, indice) => {
      const idBase = `block-content-panel-${contadorInstanciasPanel}-${idNodoSeleccionado}-${indice}`;

      if (definicion.tipo === 'expresion') {
        // `Retorno.expresion` (TSK-0209) es el único campo `'expresion'` de
        // esta tabla cuya factoría permite `null` (ver "Ampliación TSK-0209"
        // en la cabecera del archivo). A diferencia de una primera versión de
        // esta tarea (rechazada por architecture-reviewer: TSK-0209, segunda
        // ronda -- ver historial de la tarea), aquí NO se escribe nada en
        // `programaActual` solo por montar/seleccionar el bloque: hacerlo
        // materializaría `expresion: null` ("Retorno sin valor", equivalente
        // al antiguo bloque "Fin") a `expresion: Literal(0)` ("Retorno del
        // valor 0") con solo navegar hasta el bloque -- una escritura real de
        // datos con consecuencia semántica real en la ejecución
        // (`backend/src/app/domain/diagram_engine.py`) disparada por una
        // acción de SOLO LECTURA. `expresionInicial` se pasa tal cual
        // (incluido `null`) a `crearCampoExpresion`/`expression-editor.js`,
        // que ya soporta `null` de forma nativa (`determinarModoInicial`
        // arranca en el primer modo permitido, "Valor fijo", con un `input`
        // que muestra "0" por defecto): el widget YA muestra un valor
        // inicial coherente sin que el modelo tenga que guardarlo. Igual que
        // el resto de campos `'expresion'` de este panel, la escritura real
        // solo ocurre en `aplicar` (abajo), en respuesta a una edición real
        // del alumno (evento `input`/`change` real de `expression-editor.js`,
        // que nunca notifica `onCambio` en su propio montaje inicial).
        const expresionInicial = /** @type {NodoModelo | null} */ (/** @type {any} */ (nodo)[definicion.campo] ?? null);
        // Modo guiado por bloque: la condición de la Decisión y de los bucles
        // (Mientras / Repetir Hasta) usa la vista de comparación (con su
        // prefijo de frase "Si…"/"Mientras…"/"Repetir hasta que…"); el valor de
        // la Asignación usa el constructor de valor ('valor'). El resto de
        // campos de expresión (Salida, Retorno, argumentos) siguen en técnico.
        const contextoGuiado =
          nodo.nodo === TIPO_NODO.CONDICIONAL
            ? 'condicion'
            : nodo.nodo === TIPO_NODO.BUCLE_MIENTRAS
              ? 'condicion-mientras'
              : nodo.nodo === TIPO_NODO.REPETIR_HASTA
                ? 'condicion-hasta'
                : nodo.nodo === TIPO_NODO.ASIGNACION
                  ? 'valor'
                  : null;
        const campo = crearCampoExpresion({
          idBase,
          etiqueta: definicion.etiqueta,
          expresionInicial,
          variablesDisponibles,
          modoPresentacion: contextoGuiado ? MODO_PRESENTACION.GUIADO : MODO_PRESENTACION.TECNICO,
          contextoDidactico: contextoGuiado,
          aplicar: (nuevaExpresion) => {
            actualizarSentencia(idNodoSeleccionado, (actual) => ({ ...actual, [definicion.campo]: nuevaExpresion }));
            anunciar(`${definicion.etiqueta} actualizada.`);
          },
        });
        cuerpoCampos.append(campo.elemento);
        destructoresCamposActuales.push(campo.destruir);
        return;
      }

      if (definicion.tipo === 'tipo-primitivo-opcional') {
        const valorInicialTipo = /** @type {any} */ (nodo)[definicion.campo];
        const campo = crearCampoTipoPrimitivoOpcional({
          idBase,
          etiqueta: definicion.etiqueta,
          valorInicial: typeof valorInicialTipo === 'string' ? valorInicialTipo : null,
          aplicar: (nuevoTipo) => {
            actualizarSentencia(idNodoSeleccionado, (actual) => ({ ...actual, [definicion.campo]: nuevoTipo }));
            /** @type {Record<string, string>} */
            const etiquetas = ETIQUETA_TIPO_PRIMITIVO;
            anunciar(
              nuevoTipo
                ? `${definicion.etiqueta.replace(' (opcional)', '')} actualizado a "${etiquetas[nuevoTipo]}".`
                : `${definicion.etiqueta.replace(' (opcional)', '')} eliminado (sin declarar).`,
            );
          },
        });
        cuerpoCampos.append(campo.elemento);
        destructoresCamposActuales.push(campo.destruir);
        return;
      }

      if (definicion.tipo === 'texto-opcional') {
        // `Llamada.variable_destino` (TSK-0216, DEC-075): ver "Ampliación
        // TSK-0216" en la cabecera del archivo. Reutiliza `crearCampoTexto`
        // SIN modificarlo (mismo control que `nombre_funcion`), con
        // `validarIdentificadorOpcional` (acepta la cadena vacía) -- la
        // traducción "vacío -> `null`" ocurre aquí, en `aplicar`, nunca
        // dentro de `crearCampoTexto` ni del validador.
        const valorCampo = /** @type {any} */ (nodo)[definicion.campo];
        const valorInicial = typeof valorCampo === 'string' ? valorCampo : '';
        const campo = crearCampoTexto({
          idBase,
          etiqueta: definicion.etiqueta,
          valorInicial,
          validar: validarIdentificadorOpcional,
          aplicar: (valor) => {
            const nuevoValor = valor.trim().length === 0 ? null : valor;
            actualizarSentencia(idNodoSeleccionado, (actual) => ({ ...actual, [definicion.campo]: nuevoValor }));
            anunciar(
              nuevoValor
                ? `${definicion.etiqueta} actualizado a "${nuevoValor}".`
                : `${definicion.etiqueta} vaciado: el valor devuelto no se guardará en ninguna variable.`
            );
          },
        });
        cuerpoCampos.append(campo.elemento);
        destructoresCamposActuales.push(campo.destruir);
        return;
      }

      if (definicion.tipo === 'lista-expresiones') {
        const argumentosIniciales = /** @type {any} */ (nodo)[definicion.campo];
        const campo = crearCampoListaExpresiones({
          idBase,
          etiqueta: definicion.etiqueta,
          argumentosIniciales: Array.isArray(argumentosIniciales) ? argumentosIniciales : [],
          variablesDisponibles,
          aplicarLista: (nuevosArgumentos) => {
            actualizarSentencia(idNodoSeleccionado, (actual) => ({ ...actual, [definicion.campo]: nuevosArgumentos }));
          },
          anunciar,
        });
        cuerpoCampos.append(campo.elemento);
        destructoresCamposActuales.push(campo.destruir);
        return;
      }

      const valorInicial = String(/** @type {any} */ (nodo)[definicion.campo] ?? '');
      const campo = crearCampoTexto({
        idBase,
        etiqueta: definicion.etiqueta,
        valorInicial,
        validar: definicion.identificador ? validarIdentificador : validarTextoLibre,
        aplicar: (valor) => {
          actualizarSentencia(idNodoSeleccionado, (actual) => ({ ...actual, [definicion.campo]: valor }));
          anunciar(`${definicion.etiqueta} actualizado a "${valor}".`);
        },
      });
      cuerpoCampos.append(campo.elemento);
      destructoresCamposActuales.push(campo.destruir);
    });

    anunciar(`Mostrando el contenido de ${obtenerEtiqueta(nodo)}.`);
  }

  /**
   * Selección programática (misma superficie que espera un futuro
   * controlador, ver nota de la tarea sobre TSK-0051; también la usa
   * internamente `manejarFocusInCanvas`/`manejarClickCanvas`, TSK-0088).
   * @param {string | null} id
   */
  function seleccionarBloque(id) {
    const nuevoId = id ?? null;
    if (nuevoId === idSeleccionadoEnCanvas) return;
    idSeleccionadoEnCanvas = nuevoId;
    renderizarCampos();
    // TSK-0088: notifica CUALQUIER cambio real de selección (abrir, cerrar,
    // cambiar de bloque), sea cual sea el origen (focusin, clic, o una
    // llamada programática externa) -- ver "onSeleccionCambiada" en la
    // cabecera del archivo.
    if (notificarSeleccionCambiada) notificarSeleccionCambiada(nuevoId);
  }

  /**
   * `true` si `evento` se originó DENTRO del propio panel (`seccion`, la
   * propia `<section class="block-content-panel">` de esta instancia).
   *
   * TSK-0088: `editor-controller.js` ahora pasa como `canvasRoot` un
   * contenedor ESTABLE (`canvasSlot`, ver "DECISIÓN 10" en su cabecera) que
   * NECESARIAMENTE también contiene a `contenidoSlot` (el envoltorio de
   * ESTA MISMA `seccion`) como hermano del canvas propiamente dicho -- antes
   * de esta tarea `canvasRoot` era siempre `canvasVista.elemento`, un
   * subárbol que NUNCA incluía el propio panel de contenido, así que este
   * caso no podía darse. Sin este guardián, escribir en un campo propio (o
   * cualquier interacción con `expression-editor.js` dentro de este panel)
   * burbujearía hasta `canvasRoot` y se interpretaría como "focusin/clic
   * fuera de cualquier bloque" -- cerrando (deseleccionando) el propio panel
   * en el que el usuario está escribiendo en ese instante.
   * @param {Event} evento
   * @returns {boolean}
   */
  function eventoDentroDelPropioPanel(evento) {
    // Cast de tipo (nunca `instanceof Node`, no declarado en
    // eslint.config.js -- mismo motivo/precedente ya documentado en
    // `editor-controller.js#capturarFocoContenido` sobre `instanceof
    // HTMLInputElement`): `Element#contains` acepta cualquier valor en
    // tiempo de ejecución y devuelve `false` para lo que no sea un `Node`.
    const objetivo = /** @type {Node | null} */ (evento.target);
    return objetivo ? seccion.contains(objetivo) : false;
  }

  /**
   * Resuelve el `data-node-id` del bloque de canvas más cercano al objetivo
   * de un evento delegado (mismo contrato que el resto del archivo: TSK-0088
   * factoriza aquí la lógica que antes solo usaba `manejarFocusInCanvas`,
   * ahora compartida también con `manejarMouseDownCanvas`/
   * `manejarClickCanvas`).
   * @param {Event} evento
   * @returns {string | null}
   */
  function idDeNodoDesdeEvento(evento) {
    const objetivo = /** @type {{ closest?: (selector: string) => Element | null } | null} */ (evento.target);
    const conId = objetivo && typeof objetivo.closest === 'function' ? objetivo.closest('[data-node-id]') : null;
    const dataset = /** @type {{ dataset?: Record<string, string | undefined> } | null} */ (conId);
    return dataset?.dataset?.nodeId ?? null;
  }

  /** @param {FocusEvent} evento */
  function manejarFocusInCanvas(evento) {
    if (eventoDentroDelPropioPanel(evento)) return;
    seleccionarBloque(idDeNodoDesdeEvento(evento));
  }

  // TSK-0088: toggle real de apertura/cierre por clic (ver cabecera del
  // archivo, sección "TSK-0088", para la explicación completa del porqué de
  // estos dos eventos y de la carrera de foco que `manejarMouseDownCanvas`
  // evita). Estado transitorio de un único gesto de clic a la vez -- no hay
  // ningún caso real en el que dos `mousedown` se solapen sin un `click`
  // intermedio, así que una única variable de módulo (por instancia) basta.
  /** @type {string | null} */
  let idBloquePulsado = null;
  let bloquePulsadoYaEstabaAbierto = false;

  /** @param {MouseEvent} evento */
  function manejarMouseDownCanvas(evento) {
    if (eventoDentroDelPropioPanel(evento)) return;
    const id = idDeNodoDesdeEvento(evento);
    idBloquePulsado = id;
    bloquePulsadoYaEstabaAbierto = id !== null && id === idSeleccionadoEnCanvas;
  }

  /** @param {MouseEvent} evento */
  function manejarClickCanvas(evento) {
    if (eventoDentroDelPropioPanel(evento)) return;
    const id = idDeNodoDesdeEvento(evento);
    if (!id) return;
    // Si el `click` corresponde al MISMO bloque que capturó el último
    // `mousedown` (el caso normal de un clic de ratón real), usa el valor
    // capturado ANTES de la carrera de foco; si no (activación por teclado,
    // sin `mousedown` previo -- o un `mousedown` que empezó sobre otro
    // bloque y terminó aquí, gesto que se trata como "no es un clic
    // completo sobre este bloque"), usa el estado en vivo, que en ese caso
    // no sufre ninguna carrera.
    const yaEstabaAbierto = id === idBloquePulsado ? bloquePulsadoYaEstabaAbierto : id === idSeleccionadoEnCanvas;
    idBloquePulsado = null;
    seleccionarBloque(yaEstabaAbierto ? null : id);
  }

  if (canvasRoot) {
    canvasRoot.addEventListener('focusin', /** @type {any} */ (manejarFocusInCanvas));
    canvasRoot.addEventListener('mousedown', /** @type {any} */ (manejarMouseDownCanvas));
    canvasRoot.addEventListener('click', /** @type {any} */ (manejarClickCanvas));
  }

  renderizarCampos();

  if (opciones.root) {
    opciones.root.append(seccion);
  }

  function destruir() {
    if (canvasRoot) {
      canvasRoot.removeEventListener('focusin', /** @type {any} */ (manejarFocusInCanvas));
      canvasRoot.removeEventListener('mousedown', /** @type {any} */ (manejarMouseDownCanvas));
      canvasRoot.removeEventListener('click', /** @type {any} */ (manejarClickCanvas));
    }
    limpiarCampos();
  }

  return {
    elemento: seccion,
    obtenerPrograma: () => programaActual,
    obtenerIdSeleccionadoEnCanvas: () => idSeleccionadoEnCanvas,
    seleccionarBloque,
    destruir,
  };
}
